import PropTypes from 'prop-types';
import {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Container, Header, Message} from 'semantic-ui-react';

import {usePreviousValue} from 'hooks';
import {addSuccessMessage} from 'state/actions';

import {updateMatomoRegistration} from '../../Common/sharedElements';

import MatomoLink from './MatomoLink';
import MatomoStatistics from './MatomoStatistics';
import RegisterInMatomo from './RegisterInMatomo';

import styles from './Matomo.module.scss';

const Matomo = ({projectType, projectName, siteUrl, siteId, siteResourceName}) => {
  const dispatchMessage = useDispatch();
  const [updateRegistrationError, setUpdateRegistrationError] = useState(null);
  // previousSiteUrl is needed for webeos use case
  const previousSiteUrl = usePreviousValue(siteUrl);

  useEffect(() => {
    async function updateRegistration() {
      if (
        siteId &&
        (projectType !== 'drupal' || projectType !== 'wordpress' || projectType !== 'shorturl') &&
        siteUrl !== previousSiteUrl
      ) {
        try {
          await updateMatomoRegistration(siteId, siteUrl);
          dispatchMessage(addSuccessMessage('The url of the Matomo registration has been updated'));
        } catch (err) {
          setUpdateRegistrationError(err.message);
        }
      }
    }

    updateRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteUrl, siteId, previousSiteUrl]);

  const renderChildren = useCallback(() => {
    if (updateRegistrationError) {
      return (
        <Message negative>
          <Message.Header>
            There was a problem updating the registration of the site in Matomo.
          </Message.Header>
          <p>{updateRegistrationError}</p>
          <p>
            Please try again or open a{' '}
            <a
              href="https://cern.service-now.com/service-portal?id=service_element&name=web-portal"
              target="_blank"
              rel="noreferrer"
            >
              ticket
            </a>
            .
          </p>
        </Message>
      );
    } else if (siteId) {
      return (
        <>
          <MatomoLink siteId={siteId} />
          <MatomoStatistics siteId={siteId} columns={projectType === 'webeos' ? 2 : 1} />
        </>
      );
    } else {
      return (
        <RegisterInMatomo
          projectType={projectType}
          projectName={projectName}
          siteUrl={siteUrl}
          siteResourceName={siteResourceName}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, projectType, projectName, siteUrl, updateRegistrationError]);

  return (
    <Container className={styles.Matomo}>
      {!['drupal', 'wordpress', 'shorturl'].includes(projectType) && (
        <Header as="h4" textAlign="center" dividing>
          Web Analytics
        </Header>
      )}
      <p>
        Matomo, formerly known as Piwik, is a free (GPL licensed) web analytics software package. It
        provides detailed reports on your website and its visitors, including the search engines and
        keywords they used, the language they speak, which pages they like, the files they download
        and so much more. Matomo (Piwik) aims to be an open source alternative to Google Analytics.
      </p>
      {renderChildren()}
    </Container>
  );
};

Matomo.propTypes = {
  projectType: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  siteUrl: PropTypes.string.isRequired,
  siteId: PropTypes.number,
  siteResourceName: PropTypes.string,
};

Matomo.defaultProps = {
  siteId: null,
};

export default Matomo;
